import amex from '@/assets/media/creditcard/amex.svg'
import dinersclub from '@/assets/media/creditcard/dinersclub.svg'
import discover from '@/assets/media/creditcard/discover.svg'
import elo from '@/assets/media/creditcard/elo.svg'
import hipercard from '@/assets/media/creditcard/hipercard.svg'
import jcb from '@/assets/media/creditcard/jcb.svg'
import mastercard from '@/assets/media/creditcard/mastercard.svg'
import visa from '@/assets/media/creditcard/visa.svg'
import useAuth from '@/contexts/useAuth'
import useEmpresa from '@/contexts/useEmpresa'
import api from '@/services/api'
import { formaPagamentoPadrao } from '@/services/fatura'
import { formatMoney, getTipoPix, validateEmail, validatePhone } from '@/utils'
import { faCreditCard, faMoneyBill, faReceipt } from '@fortawesome/free-solid-svg-icons'
import { faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons/faFileInvoiceDollar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as Sentry from '@sentry/react'
import cardValidator from 'card-validator'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Loading from '../Loading'
import type { Fatura } from '@/types/fatura'
import type { PagamentoBoleto, PagamentoCartao, PagamentoPix } from '@/types/pagamento'
import { copyTextToClipboard } from '@/utils'
import FaturaValorResumo from './FaturaValorResumo'
import AsaasBadge from './AsaasBadge'

const creditcardIcons: Record<string, string> = {
    'american-express': amex,
    'diners-club': dinersclub,
    discover,
    elo,
    hipercard,
    jcb,
    mastercard,
    visa,
}

interface PagamentoPanelProps {
    faturas: Fatura[]
    onPaymentComplete: () => Promise<void>
    clienteHash?: string
    onPaymentResult?: (result: PagamentoPix | PagamentoBoleto | PagamentoCartao) => void
    resultModalOpen?: boolean
}

export default function PagamentoPanel({
    faturas,
    onPaymentComplete,
    clienteHash: clienteHashProp,
    onPaymentResult,
    resultModalOpen = false,
}: PagamentoPanelProps) {
    const { empresa } = useEmpresa()
    const { user } = useAuth()
    const { clienteHash: clienteHashParam } = useParams<{ clienteHash?: string }>()
    const clienteHash = clienteHashProp ?? clienteHashParam

    const [formaPagamento, setFormaPagamento] = useState(formaPagamentoPadrao(empresa))
    const [cardNumberType, setCardNumberType] = useState<string>()
    const [loading, setLoading] = useState(false)
    const [loadingProofOfPayment, setLoadingProofOfPayment] = useState(false)

    const { register, handleSubmit, watch } = useForm()
    const { register: registerPix, handleSubmit: handlePixSubmit } = useForm()
    const watchCardNumber = watch('numero')

    const enablePagamento =
        empresa?.PermitePagamento &&
        (empresa.CdFormaBoleto > 0 ||
            empresa.CdFormaCartao > 0 ||
            empresa.CdFormaPix > 0 ||
            empresa.PermiteDeposito)

    useEffect(() => {
        setFormaPagamento(formaPagamentoPadrao(empresa))
    }, [faturas.length, empresa])

    useEffect(() => {
        const cardType = cardValidator.number(watchCardNumber)?.card?.type
        setCardNumberType(cardType)
    }, [watchCardNumber])

    const onError = (msg: string) => {
        toast.error(msg)
        setLoading(false)
        return true
    }

    const verifyPersonalData = () => {
        if (!user?.CPF?.replace(/\D/g, '')) return !onError('Verifique seu CPF e tente novamente!')
        if (!user?.Nome) return !onError('Verifique seu nome.')
        if (!user?.Email || !validateEmail(user.Email)) return !onError('Verifique seu email.')
        if (!user?.Telefone || !validatePhone(user.Telefone)) return !onError('Verifique seu telefone.')
        if (!user?.CEP) return !onError('Verifique seu CEP.')
        if (!user?.Logradouro) return !onError('Verifique seu logradouro.')
        if (!user?.Bairro) return !onError('Verifique seu bairro.')
        if (!user?.Numero) return !onError('Verifique o número da residência.')
        if (!user?.Cidade) return !onError('Verifique sua cidade.')
        if (!user?.Estado) return !onError('Verifique seu estado.')
        return true
    }

    const uploadProofOfPayment = async (file: File | null) => {
        if (!file) return
        setLoadingProofOfPayment(true)
        try {
            await api.postForm(
                '/clientes/faturas/pagamentos/comprovantes',
                { file, faturas: faturas.map(fatura => fatura.CdFatura) },
                { headers: { clienteHash } }
            )
            await onPaymentComplete()
            toast.success('Comprovante enviado com sucesso!')
        } catch (error) {
            toast.error('Ocorreu um erro inesperado.')
            Sentry.captureException(error)
        } finally {
            setLoadingProofOfPayment(false)
        }
    }

    const gerarBoleto = async () => {
        if (!verifyPersonalData()) return
        setLoading(true)
        try {
            const { data } = await api.post(
                '/clientes/faturas/pagamentos',
                { tipo: 'boleto', codFaturas: faturas.map(fatura => fatura.CdFatura) },
                { headers: { clienteHash } }
            )

            if (data.errors) {
                return onError(
                    data.errors
                        .map((error: any) => error.fields.map((fieldError: any) => fieldError.message).join('\n'))
                        .join('\n')
                )
            }

            onPaymentResult?.(data)
            await onPaymentComplete()
        } catch {
            onError('Ocorreu um erro inesperado.')
        } finally {
            setLoading(false)
        }
    }

    const gerarPagamentoCartao = async (cartao: any) => {
        if (loading || !verifyPersonalData()) return
        if (!cardNumberType) return onError('Número do cartão inválido ou não suportado.')

        setLoading(true)
        try {
            const { data } = await api.post(
                '/clientes/faturas/pagamentos',
                {
                    tipo: 'cartao',
                    codFaturas: faturas.map(fatura => fatura.CdFatura),
                    cartao: {
                        bandeira: cardNumberType,
                        validade: `${cartao.year}-${String(cartao.month).padStart(2, '0')}`,
                        ...cartao,
                    },
                },
                { headers: { clienteHash } }
            )

            if (data.error) return onError(data.error)
            if (data.errors) {
                return onError(
                    data.errors
                        .map((error: any) => error.fields.map((fieldError: any) => fieldError.message).join('\n'))
                        .join('\n')
                )
            }

            onPaymentResult?.(data)
            await onPaymentComplete()
        } catch {
            onError('Ocorreu um erro inesperado.')
        } finally {
            setLoading(false)
        }
    }

    const gerarPix = async (dadosPessoais: any) => {
        if (!dadosPessoais.cpfCnpj?.replace(/\D/g, '')) return onError('Verifique seu CPF e tente novamente!')
        if (!dadosPessoais.nomeCompleto) return onError('Verifique seu nome.')
        if (!dadosPessoais.telefone || dadosPessoais.telefone.length < 10) {
            return onError('O número de telefone deve conter pelo menos 10 dígitos (DDD + número).')
        }

        setLoading(true)
        try {
            const { data } = await api.post(
                '/clientes/faturas/pagamentos',
                { tipo: 'pix', codFaturas: faturas.map(fatura => fatura.CdFatura), ...dadosPessoais },
                { headers: { clienteHash } }
            )

            if (data.message) onError(data.message)
            if (data.action === 'reload') {
                onPaymentResult?.(data)
                await onPaymentComplete()
                return
            }
            if (data.error) return onError(data.error)
            if (data.errors) {
                return onError(
                    data.errors
                        .map((error: any) => error.fields.map((fieldError: any) => fieldError.message).join('\n'))
                        .join('\n')
                )
            }

            onPaymentResult?.(data)
            await onPaymentComplete()
        } catch {
            onError('Ocorreu um erro inesperado.')
        } finally {
            setLoading(false)
        }
    }

    const copyPixField = async () => {
        const copied = await copyTextToClipboard(user?.ContaBancariaEmpresa.PIX ?? '')
        if (copied) {
            toast.success('PIX copiado com sucesso!')
            return
        }

        toast.error('Não foi possível copiar o PIX.')
    }

    if (loading) {
        return (
            <div className="pagamento-panel">
                <Loading type="primary" />
            </div>
        )
    }

    return (
        <div className="pagamento-panel">
            {loadingProofOfPayment && <Loading type="primary" wrapperClass="modal-overlay" />}

            <div className="pagamento-panel-header">
                <h4 className="pagamento-panel-title">Pagamento</h4>
            </div>

            {faturas.length > 0 && (
                <FaturaValorResumo
                    faturas={faturas}
                    variant="panel"
                    className="pagamento-panel-valores"
                    metodo={formaPagamento}
                    incluirRepasse
                />
            )}

            {!enablePagamento && (
                <p className="text-muted" style={{ fontSize: 13 }}>
                    Pagamento online não disponível. Entre em contato com a empresa.
                </p>
            )}

            {enablePagamento && faturas.length === 0 && (
                <p className="text-muted" style={{ fontSize: 13 }}>
                    Nenhuma fatura em aberto no período.
                </p>
            )}

            {enablePagamento && faturas.length > 0 && (
                <>
                    <div className="pagamento-metodos">
                        {empresa?.CdFormaPix > 0 && (
                            <button type="button" className={`pagamento-metodo-btn${formaPagamento === 'pix' ? ' active' : ''}`} onClick={() => setFormaPagamento('pix')}>
                                Pix
                            </button>
                        )}
                        {empresa?.CdFormaBoleto > 0 && (
                            <button type="button" className={`pagamento-metodo-btn${formaPagamento === 'boleto' ? ' active' : ''}`} onClick={() => setFormaPagamento('boleto')}>
                                Boleto
                            </button>
                        )}
                        {empresa?.CdFormaCartao > 0 && (
                            <button type="button" className={`pagamento-metodo-btn${formaPagamento === 'cartao' ? ' active' : ''}`} onClick={() => setFormaPagamento('cartao')}>
                                Cartão
                            </button>
                        )}
                        {empresa?.PermiteDeposito && (
                            <button type="button" className={`pagamento-metodo-btn${formaPagamento === 'bank_deposit' ? ' active' : ''}`} onClick={() => setFormaPagamento('bank_deposit')}>
                                Depósito
                            </button>
                        )}
                    </div>

                    {formaPagamento === 'bank_deposit' && (
                        <div className={`pagamento-form-stack${resultModalOpen ? ' is-disabled' : ''}`} aria-hidden={resultModalOpen}>
                            <p className="pagamento-deposito-note">
                                Confira os dados para o depósito e envie o comprovante abaixo.
                            </p>
                            <div className="pagamento-deposito-grid">
                                <div className="pagamento-deposito-field">
                                    <label className="form-label">Titular</label>
                                    <input type="text" className="form-control deposito-field-input" disabled readOnly value={user?.ContaBancariaEmpresa.TitularConta ?? ''} />
                                </div>
                                <div className="pagamento-deposito-field">
                                    <label className="form-label">Banco</label>
                                    <input type="text" className="form-control deposito-field-input" disabled readOnly value={user?.ContaBancariaEmpresa.Banco ?? ''} />
                                </div>
                                <div className="pagamento-deposito-field">
                                    <label className="form-label">PIX</label>
                                    <div className="copy-field-inline">
                                        <input type="text" className="form-control deposito-field-input" disabled readOnly value={user?.ContaBancariaEmpresa.PIX ?? ''} />
                                        <button type="button" className="btn btn-outline-primary copy-field-btn copy-field-btn-inline" onClick={copyPixField} disabled={resultModalOpen}>
                                            Copiar PIX
                                        </button>
                                    </div>
                                </div>
                                <div className="pagamento-deposito-field">
                                    <label className="form-label">Tipo PIX</label>
                                    <input type="text" className="form-control deposito-field-input" disabled readOnly value={getTipoPix(user?.ContaBancariaEmpresa.TipoPix)} />
                                </div>
                            </div>
                            <div className="pagamento-actions">
                                <label className={`btn btn-outline-primary pagamento-upload-btn${resultModalOpen ? ' is-disabled' : ''}`}>
                                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" />
                                    Selecionar comprovante
                                    <input className="hidden" type="file" onChange={e => e.target.files && uploadProofOfPayment(e.target.files[0])} disabled={loadingProofOfPayment || resultModalOpen} />
                                </label>
                            </div>
                        </div>
                    )}

                    {formaPagamento === 'boleto' && (
                        <button type="button" onClick={gerarBoleto} className="btn btn-primary w-full pagamento-submit-btn">
                            <FontAwesomeIcon icon={faReceipt} className="mr-2" /> Gerar Boleto
                        </button>
                    )}

                    {formaPagamento === 'cartao' && (
                        <form onSubmit={handleSubmit(gerarPagamentoCartao)} className="pagamento-form-stack">
                            <div className="pagamento-form-grid">
                                <div className="form-group">
                                    <label className="form-label">Nome no cartão</label>
                                    <input type="text" className="form-control" {...register('titular', { required: true })} />
                                </div>
                            </div>
                            <div className="pagamento-form-grid pagamento-form-grid-2">
                                <div className="form-group">
                                    <label className="form-label">Número do cartão</label>
                                    <input
                                        type="number"
                                        className="form-control card-number"
                                        style={{
                                            backgroundImage:
                                                cardNumberType && creditcardIcons[cardNumberType]
                                                    ? `url('${creditcardIcons[cardNumberType]}')`
                                                    : undefined,
                                        }}
                                        {...register('numero', { required: true, maxLength: 16 })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">CVV</label>
                                    <input type="number" className="form-control" {...register('cvv', { required: true })} />
                                </div>
                            </div>
                            <div className="pagamento-form-grid pagamento-form-grid-2">
                                <div className="form-group">
                                    <label className="form-label">Mês</label>
                                    <select {...register('month')} className="custom-select form-control">
                                        {[
                                            'Janeiro', 'Fevereiro', 'Março', 'Abril',
                                            'Maio', 'Junho', 'Julho', 'Agosto',
                                            'Setembro', 'Outubro', 'Novembro', 'Dezembro',
                                        ].map((mes, index) => (
                                            <option key={mes} value={index + 1}>
                                                {mes}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Ano</label>
                                    <select {...register('year')} className="custom-select form-control">
                                        {[...Array(20).keys()].map((num) => (
                                            <option key={num} value={new Date().getFullYear() + num}>
                                                {new Date().getFullYear() + num}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="pagamento-switch-row">
                                <input type="checkbox" id="recorrente" {...register('recorrente')} />
                                <label htmlFor="recorrente" className="mb-0">
                                    Tornar o Faturamento Automático e Recorrente
                                </label>
                            </div>
                            <button type="submit" className="btn btn-primary w-full pagamento-submit-btn" disabled={loading}>
                                <FontAwesomeIcon icon={faCreditCard} className="mr-2" /> Pagar agora
                            </button>
                        </form>
                    )}

                    {formaPagamento === 'pix' && (
                        <form onSubmit={handlePixSubmit(gerarPix)} className="pagamento-form-stack">
                            <div className="pagamento-form-grid">
                                <div className="form-group">
                                    <label className="form-label">Nome completo</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        {...registerPix('nomeCompleto', { required: true, value: user?.Nome })}
                                    />
                                </div>
                            </div>
                            <div className="pagamento-form-grid">
                                <div className="form-group">
                                    <label className="form-label">CPF</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        {...registerPix('cpfCnpj', { required: true, value: user?.CPF })}
                                    />
                                </div>
                            </div>
                            <div className="pagamento-form-grid">
                                <div className="form-group">
                                    <label className="form-label">Telefone</label>
                                    <input
                                        type="tel"
                                        className="form-control"
                                        {...registerPix('telefone', { required: true, value: user?.Telefone })}
                                    />
                                </div>
                            </div>
                            <button type="submit" className="btn btn-primary w-full pagamento-submit-btn" disabled={loading}>
                                <FontAwesomeIcon icon={faMoneyBill} className="mr-2" /> Gerar Pix
                            </button>
                        </form>
                    )}
                </>
            )}

            {(enablePagamento && faturas.length > 0) && <AsaasBadge />}
        </div>
    )
}
